import { MapFilterType, ReplaceDomainNodeType, replaceSearchFieldDummyId } from 'app/components/filters/filter-replacements/constants';
import { deepEquals } from 'app/infrastructure/js';
import {
  ComplexValue,
  FavouriteReplaceDomainNodes
} from 'app/types-shared';
import { ValidationLevel, ValidationProps } from 'ui';

export const getReplaceDomainNodes = (type: ReplaceDomainNodeType,
  replaceDomainNodes: FavouriteReplaceDomainNodes[] = []): FavouriteReplaceDomainNodes[] => {
  if (!replaceDomainNodes) {
    return [];
  }

  if (type === ReplaceDomainNodeType.Default) {
    return replaceDomainNodes.filter(node => node.replaceDomainNode.v !== replaceSearchFieldDummyId);
  } else {
    return replaceDomainNodes.filter(node => node.replaceDomainNode.v === replaceSearchFieldDummyId);
  }
};

export const mapDummyReplaceDomainNodes = (
  currentDummyNodes: ComplexValue<number>[],
  replaceDomainNodes: FavouriteReplaceDomainNodes[] = []
): FavouriteReplaceDomainNodes[] => {

  const defaultNodes = getReplaceDomainNodes(ReplaceDomainNodeType.Default, replaceDomainNodes);

  const mappedDummyNodes = currentDummyNodes.map(node => {
    return {
      domainNode: { v: node.value, d: node.description },
      replaceDomainNode: { v: replaceSearchFieldDummyId }
    };
  });

  return [...defaultNodes, ...mappedDummyNodes];
};

export const mapDefaultReplaceDomainNodesPerType = (
  index: number,
  value: ComplexValue<number>,
  type: MapFilterType,
  replaceDomainNodes: FavouriteReplaceDomainNodes[] = []): FavouriteReplaceDomainNodes[] => {

  if (index < 0 || index >= replaceDomainNodes.length) {
    return replaceDomainNodes;
  }

  const defaultNodes = getReplaceDomainNodes(ReplaceDomainNodeType.Default, replaceDomainNodes);
  const dummyNodes = getReplaceDomainNodes(ReplaceDomainNodeType.Dummy, replaceDomainNodes);

  defaultNodes[index] = {
    ...defaultNodes[index],
    [type]: value ? { v: value.value, d: value.description } : { v: 0, d: '' }
  };

  return [...defaultNodes, ...dummyNodes];
};

export const removeDefaultReplaceDomainNode = (
  index: number,
  replaceDomainNodes: FavouriteReplaceDomainNodes[] = []
): FavouriteReplaceDomainNodes[] => {

  const defaultNodes = getReplaceDomainNodes(ReplaceDomainNodeType.Default, replaceDomainNodes);
  const dummyNodes = getReplaceDomainNodes(ReplaceDomainNodeType.Dummy, replaceDomainNodes);

  return [
    ...defaultNodes.filter((n, i) => i !== index),
    ...dummyNodes
  ];
};

export const isAddReplaceDomainNodeDisabled = (
  replaceDomainNodes: FavouriteReplaceDomainNodes[] = []
): boolean => {

  return replaceDomainNodes.some(node =>
    node.domainNode?.v === 0 || node.replaceDomainNode?.v === 0
  );
};

export const isReadOnlyReplaceDomainNode = (
  replaceDomainNode: FavouriteReplaceDomainNodes
): boolean => {
  return replaceDomainNode?.domainNode?.v !== 0;
};

export const hasReplaceDomainNodesChanged = (
  originalNodes: FavouriteReplaceDomainNodes[] = [],
  currentNodes: FavouriteReplaceDomainNodes[] = []
): boolean => {

  const filteredOriginalNodes = originalNodes
    .filter(node => node?.domainNode?.v !== 0)
    .map(node => ({
      domainNode: { v: node.domainNode?.v },
      replaceDomainNode: { v: node.replaceDomainNode?.v }
    })).sort((a, b) => a.domainNode.v - b.domainNode.v);

  const filteredCurrentNodes = currentNodes
    .filter(node => node?.replaceDomainNode?.v !== 0)
    .map(node => ({
      domainNode: { v: node.domainNode?.v },
      replaceDomainNode: { v: node.replaceDomainNode?.v }
    })).sort((a, b) => a.domainNode.v - b.domainNode.v);

  return !deepEquals(filteredOriginalNodes, filteredCurrentNodes);
};

export const getSelectedDefaultOptionIds =
  (replaceDomainNodes: FavouriteReplaceDomainNodes[]): number[] => {
    const defaultDomainNodes = getReplaceDomainNodes(ReplaceDomainNodeType.Default, replaceDomainNodes);
    const selectedDomainNodes: number[] = defaultDomainNodes.map(node => node?.domainNode?.v);
    return selectedDomainNodes;
  };

export const getAllOptionIds = (replaceDomainNodes: FavouriteReplaceDomainNodes[]): number[] => {
  return replaceDomainNodes
    .filter(node => node?.domainNode?.v !== 0)
    .map(node => node?.domainNode?.v);
};

export const shouldReloadReplaceDomainNodes = (
  previousReplaceDomainNodes: FavouriteReplaceDomainNodes[] = [],
  currentReplaceDomainNodes: FavouriteReplaceDomainNodes[] = []
): boolean => {
  const previousOptionIds = getAllOptionIds(previousReplaceDomainNodes);
  const currentOptionIds = getAllOptionIds(currentReplaceDomainNodes);
  return !deepEquals(previousOptionIds, currentOptionIds);
};

export const getDefaultDomainNodes = (replaceDomainNodes: FavouriteReplaceDomainNodes[] = []
): FavouriteReplaceDomainNodes[] => {
  return getReplaceDomainNodes(ReplaceDomainNodeType.Default, replaceDomainNodes);
};

export const getDummyDomainNodes = (replaceDomainNodes: FavouriteReplaceDomainNodes[] = []
): FavouriteReplaceDomainNodes[] => {
  return getReplaceDomainNodes(ReplaceDomainNodeType.Dummy, replaceDomainNodes);
};

export const getOptionsIdsForRemoveFilters = (
  replaceDomainNodes: FavouriteReplaceDomainNodes[]
): number[] => {
  const dummyNodes = getDummyDomainNodes(replaceDomainNodes);
  const getDummyNodeIds = getAllOptionIds(dummyNodes);
  return getAllOptionIds(replaceDomainNodes).filter(node => !getDummyNodeIds.includes(node));
};

export const validateReplaceDomainNodes = (
  replaceDomainNodes: FavouriteReplaceDomainNodes[],
  errorMessage: string
): ValidationProps[] => {
  const domainNode = replaceDomainNodes
    .map((value, index) => value?.domainNode?.v === 0 ? index : null)
    .filter(index => index !== null);
  const replaceDomainNode = replaceDomainNodes
    .map((value, index) => value?.replaceDomainNode?.v === 0 ? index : null)
    .filter(index => index !== null);

  const domainNodesValidation = domainNode.map(index => ({
    level: ValidationLevel.error,
    messages: [
      {
        level: ValidationLevel.error,
        message: errorMessage,
        parameters: {
          domainNode: index
        }
      }
    ]
  }));

  const replaceDomainNodesValidation = replaceDomainNode.map(index => ({
    level: ValidationLevel.error,
    messages: [
      {
        level: ValidationLevel.error,
        message: errorMessage,
        parameters: {
          replaceDomainNode: index
        }
      }
    ]
  }));

  return [...domainNodesValidation, ...replaceDomainNodesValidation];
};

export const getReplaceDomainNodesValidation = (index: number, validation: ValidationProps[]): ValidationProps[] => {
  return validation
    .filter(item => {
      return item.messages?.[0]?.parameters?.[MapFilterType.DomainNode] !== index &&
        item.messages?.[0]?.parameters?.[MapFilterType.ReplaceDomainNode] !== index;
    })
    .map(item => {
      const parameters = item.messages?.[0]?.parameters;
      if (!parameters) {
        return item;
      }

      const newParameters = { ...parameters };

      if (parameters[MapFilterType.DomainNode] > index) {
        newParameters[MapFilterType.DomainNode] = parameters[MapFilterType.DomainNode] - 1;
      }

      if (parameters[MapFilterType.ReplaceDomainNode] > index) {
        newParameters[MapFilterType.ReplaceDomainNode] = parameters[MapFilterType.ReplaceDomainNode] - 1;
      }

      return {
        ...item,
        messages: [{
          ...item.messages?.[0],
          parameters: newParameters
        }]
      };
    });
};
